// ---------------------------------------------------------------------------
// V22 — WHAT A PROOF PACK ROW SAYS, AS A PURE FUNCTION.
//
// Same shape as `system/timesheet.ts` and for the same reason: the sentence a row is drawn
// with is the thing most worth unit-testing and the thing least testable inside a
// component. A screen that decided this inline would be a screen whose states are only
// checkable by looking at it.
//
// AND STATUS IS NEVER A COLOUR ALONE. Every state returns a tone AND words, because
// `11-console-design-system.md` requires it and because the reader here is a compliance
// officer scanning twenty packs for the one that was superseded.
// ---------------------------------------------------------------------------

import { english, type Translate } from "../i18n/translate";

export type ProofPackRow = {
  pack_id: string;
  issued_at: string;
  algorithm: string;
  key_id: string;
  merkle_root: string;
  leaf_count: number;
  content_sha256: string;
  supersedes_id: string | null;
  superseded_by: string | null;
  subject_count: number;
  is_superseded: boolean;
};

export type ChipTone = "live" | "attention" | "failed" | "neutral";

/**
 * SUPERSEDED IS `attention`, NOT `failed`.
 *
 * §5: a superseded pack "is still verifiable and is marked superseded". It is not broken
 * and it is not wrong — it is the earlier account, and it remains signed evidence of what
 * was said at the time. Red would tell a reader to distrust a document they may need.
 */
export function proofPackState(row: ProofPackRow, t: Translate = english): { tone: ChipTone; label: string } {
  if (row.is_superseded) return { tone: "attention", label: t("Superseded by a later pack") };
  if (row.supersedes_id !== null) return { tone: "live", label: t("Current — a correction") };
  return { tone: "live", label: t("Current") };
}

/**
 * Whether a recomputation still agrees with what was signed.
 *
 * THIS IS §6.2 AS A SENTENCE. If a source audit row has been altered since the pack was
 * issued, the chain recomputes to a different root and the signature — which covers the
 * root — is now a statement about history that no longer exists.
 *
 * The disagreement is NOT necessarily tampering, and the wording is careful about that: an
 * event that has carried on since the pack was issued has new audit rows, so a later pack
 * is the ordinary answer. What the screen must never do is claim a forgery.
 */
export function chainAgreement(
  row: Pick<ProofPackRow, "merkle_root" | "content_sha256" | "leaf_count">,
  recomputed: {
    merkle_root: string;
    content_sha256: string;
    leaf_count: number;
    /** 0230 (S107). The same records hashed as each format would have built them. */
    content_sha256_by_format?: Record<string, string>;
  } | null,
  t: Translate = english,
): { tone: ChipTone; label: string } {
  if (recomputed === null) return { tone: "neutral", label: t("Not recomputed") };
  // A PACK IS COMPARED UNDER THE FORMAT IT WAS ISSUED IN. A format 1 pack of unchanged
  // records hashes to the format 1 content, and calling it "no longer produced" because
  // the product later added a section would be a false alarm about a sound document.
  const hashes = [recomputed.content_sha256, ...Object.values(recomputed.content_sha256_by_format ?? {})];
  if (recomputed.merkle_root === row.merkle_root && hashes.includes(row.content_sha256)) {
    return { tone: "live", label: t("Matches the source records exactly") };
  }
  if (recomputed.leaf_count > row.leaf_count) {
    return {
      tone: "attention",
      label: t("The event has {n} newer record(s) — issue a correcting pack", { n: recomputed.leaf_count - row.leaf_count }),
    };
  }
  return { tone: "failed", label: t("The source records no longer produce this pack") };
}

/** Short enough to read across a table cell, long enough to compare by eye. */
export function shortHash(hex: string): string {
  return hex.length <= 16 ? hex : `${hex.slice(0, 8)}…${hex.slice(-8)}`;
}
